import { apiClient } from '../api-client';
import type {
  ExportDownloadResponse,
  ExportJob,
  ExportListResponse,
  StartExportRequest,
} from '../../dtos/admin/export.dto';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * Patient and agency data exports (Phase 14, DAT-01..06). The backend builds a ZIP of PDF + CSV
 * files asynchronously; the client starts a job, polls its status, then asks for a short-lived
 * download link. Errors are ApiErrors with domain codes (PURPOSE_REQUIRED, REAUTH_REQUIRED,
 * EXPORT_IN_PROGRESS, EXPORT_EXPIRED, AUDIT_WRITE_FAILED…); use describeApiError to show them.
 */
export class ExportService {
  static async startPatientExport(patientId: string, request: StartExportRequest): Promise<ExportJob> {
    const response = await apiClient.post<ExportJob>(`/patients/${encodeURIComponent(patientId)}/export`, request);
    if (!response.data) throw new Error('The export did not start');
    return response.data;
  }

  static async startAgencyExport(request: Omit<StartExportRequest, 'kind'>): Promise<ExportJob> {
    const response = await apiClient.post<ExportJob>('/agency/export', request);
    if (!response.data) throw new Error('The export did not start');
    return response.data;
  }

  static async list(patientId?: string): Promise<ExportJob[]> {
    const response = await apiClient.get<ExportListResponse>('/exports', patientId ? { patientId } : undefined);
    return response.data?.exports ?? [];
  }

  static async get(exportId: string): Promise<ExportJob> {
    const response = await apiClient.get<ExportJob>(`/exports/${encodeURIComponent(exportId)}`);
    if (!response.data) throw new Error('Export not found');
    return response.data;
  }

  /** Poll until the job is complete or failed (or the timeout passes). */
  static async waitForCompletion(
    exportId: string,
    { intervalMs = POLL_INTERVAL_MS, timeoutMs = POLL_TIMEOUT_MS, signal }: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {}
  ): Promise<ExportJob> {
    const deadline = Date.now() + timeoutMs;
    for (;;) {
      if (signal?.aborted) throw new Error('cancelled');
      const job = await ExportService.get(exportId);
      if (job.status === 'complete' || job.status === 'failed') return job;
      if (Date.now() > deadline) return job;
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  static async getDownloadLink(exportId: string): Promise<ExportDownloadResponse> {
    const response = await apiClient.get<ExportDownloadResponse>(`/exports/${encodeURIComponent(exportId)}/download`);
    if (!response.data) throw new Error('No download link was returned');
    return response.data;
  }

  /**
   * Download through the presigned link. The link points at S3 directly (no auth header); the
   * file name comes from the link's Content-Disposition.
   */
  static async download(exportId: string): Promise<string> {
    const { downloadUrl, fileName } = await ExportService.getDownloadLink(exportId);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.rel = 'noopener';
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return fileName;
  }
}

export default ExportService;
